// src/features/developers/recipes.ts
//
// The samples of the «Ejemplos» tab: one per TASK an integrator does, in the order they meet them.
// Each is one description of a call (see samples.ts); the five languages are generated from it.
// The first is the quick start's main call, so it is the one that needs the fewest fields.
//
// Values are the ones the OpenAPI examples use, so the reference, the Postman collection and the
// samples tell one story. The event batch is taken from the OpenAPI document itself when it has
// arrived (and from a built-in copy of the same example when it has not).

import { httpSamples, type HttpCall } from './samples';
import type { RecipeLanguage } from './types';

/** The property of the OpenAPI examples. */
export const EXAMPLE_PROPERTY_ID = '3f9c2a6e-4b1d-4c7a-9e2f-1a2b3c4d5e6f';

export interface Recipe {
  key: string;
  /** Text key (under `developers.recipes.<key>`): `.title` and `.description`. */
  endpoint: string;
  call: HttpCall;
}

/** The same event as the OpenAPI example, for when the document has not been fetched. */
const FALLBACK_EVENT_BATCH: unknown[] = [
  {
    specversion: '1.0',
    id: '01J8ZJ5M0N3Q4R5S6T7V8W9X0Y',
    source: '/partner-acme/reservations-1',
    type: 'com.roommatik.fact.reservation.created',
    subject: 'property/' + EXAMPLE_PROPERTY_ID + '/reservation/88213',
    time: '2026-09-27T08:15:02.314Z',
    datacontenttype: 'application/json',
    sequence: '1042',
    propertyid: EXAMPLE_PROPERTY_ID,
    propertylocaldate: '2026-09-27',
    data: {
      pms_reservation_id: '88213',
      status: 'confirmed',
      arrival_date: '2026-09-27',
      departure_date: '2026-09-29',
      party: { adults: 2, children: 0 },
      rooms_count: 1,
      commercial_origin: 'OTA',
      event_time: '2026-09-27T08:14:51Z',
      observed_at: '2026-09-27T08:15:02.301Z',
      observed_via: 'push',
    },
  },
];

interface OpenApiLike {
  paths?: Record<string, { post?: { requestBody?: { content?: Record<string, { example?: unknown }> } } }>;
}

/** The batch the OpenAPI document gives as the example of «Send a batch of events». */
export const eventBatchOf = (spec: unknown): unknown[] => {
  const content = (spec as OpenApiLike | null)?.paths?.['/api/v2/events']?.post?.requestBody?.content;
  const example = content?.['application/cloudevents-batch+json']?.example;
  return Array.isArray(example) && example.length > 0 ? example : FALLBACK_EVENT_BATCH;
};

export const buildRecipes = (spec: unknown = null): Recipe[] => [
  {
    key: 'properties',
    endpoint: 'GET /api/v2/data/properties',
    call: { method: 'GET', path: '/api/v2/data/properties', credential: 'apiKey', show: 'data' },
  },
  {
    key: 'indicators',
    endpoint: 'GET /api/v2/data/properties/{propertyId}/metrics',
    call: {
      method: 'GET',
      path: '/api/v2/data/properties/' + EXAMPLE_PROPERTY_ID + '/metrics?from=2026-09-01&to=2026-09-30&metrics=ops.arrivals,ops.containment,ops.time_to_key_median',
      credential: 'apiKey',
      show: 'data.metrics',
    },
  },
  {
    key: 'checkins',
    endpoint: 'GET /api/v2/data/properties/{propertyId}/check-ins',
    call: {
      method: 'GET',
      path: '/api/v2/data/properties/' + EXAMPLE_PROPERTY_ID + '/check-ins?from=2026-09-01&to=2026-09-07&pageSize=50',
      credential: 'apiKey',
      paged: true,
    },
  },
  {
    key: 'events',
    endpoint: 'POST /api/v2/events',
    call: {
      method: 'POST',
      path: '/api/v2/events',
      credential: 'sourceKey',
      contentType: 'application/cloudevents-batch+json',
      body: eventBatchOf(spec),
      note: 'A batch is a JSON array of up to 500 events. Sending the same event id again is safe: it is reported as a duplicate.',
    },
  },
];

export interface RenderedRecipe extends Recipe {
  code: Record<RecipeLanguage, string>;
}

export const renderRecipes = (spec: unknown = null): RenderedRecipe[] =>
  buildRecipes(spec).map((recipe) => ({ ...recipe, code: httpSamples(recipe.call) }));
